// How much of a branch the viewer has already reviewed, as the Branches
// tab shows it: the changed lines of its comparison that the viewer's
// review marks cover, counted by the review model (review/model.ts), the
// one count every view of "is this reviewed" reads, so it is the compare
// page's count wherever they see the same lines.
//
// What it reads is the comparison's file list (GitHub's compare: each
// file's path, old name, status, blobs, line counts and patch), the old
// blobs at the merge base (asked a hundred paths to a query, never one
// request per file), and the marks on those blobs. Each file becomes the
// model's `ReviewFile`: its sides (path, blob, and its old name, whose
// marks apply to it), and its changes from GitHub's patch, whose hunks
// number removed lines on the old side and added lines on the new, as
// the diff model anchors them. A file with no line changed (a rename
// without edits, a mode change) has no changes, so nothing to review, as
// on the compare page.
//
// Where the compare page (or the pull request page) has already computed
// a file and kept its status (state/reviewStatus.ts, by its subject, the
// diff options and the file's base…head identity, `baseHeadIdentity`),
// that status is the file's figure: it counts its engine diff and marks
// carried from an earlier blob by a line map, which the patch and the
// marks on these exact blobs do not see. It is taken only when it was
// computed for the marks on the file's paths now (`pathMarksSignature`,
// as the Files view checks its own), so it never counts a mark removed
// since. The store looks up the file's blobs along the comparison's
// commits for this, as the compare page does.
//
// A changed file with no patch (binary, or too large) that the compare
// page has not kept counts its lines as unreviewed. GitHub's comparison
// lists 300 files at most: past that, a pull request's counts give the
// lines of the files not listed, counted as unreviewed; a branch without
// one has no such count, so its figure is of the first 300 files only
// (`firstFiles`).
import type { Anchor } from '../review/anchors';
import { reviewModel, type ReviewChange, type ReviewFile } from '../review/model';
import { baseHeadIdentity, pathMarksSignature, type FileStatus } from '../state/reviewStatus';

/** One changed file of the comparison, as GitHub's compare lists it. */
export interface BranchFile {
  path: string;
  /** Its old name when renamed. */
  from: string | null;
  status: string;
  /** The new blob; null when removed. */
  blob: string | null;
  additions: number;
  deletions: number;
  patch: string | null;
}

/** GitHub's comparison lists this many files at most. */
export const LISTED_FILES_MAX = 300;

/** The share reviewed: changed lines the marks cover (`done`) of all (`total`). */
export interface ReviewedShare {
  done: number;
  total: number;
  /** Every mark it counts is known. */
  known: boolean;
  /** Files past the 300 GitHub lists are not counted, and nothing says how many lines they hold: the share is of the first 300 files only. */
  firstFiles: boolean;
  /**
   * A file has marks on an earlier blob (found along the commits) that no
   * kept status carries: the compare page carries them by line maps, which
   * need the texts, so here the share is at least this.
   */
  lowerBound: boolean;
  /** Its earlier versions were not looked up (the tab's budget, or GitHub's, was low): counted from marks on these versions only. */
  theseVersions?: true;
}

/** What is known of the files GitHub's comparison does not list: none, their lines (a pull request's counts), or not how many. */
export type Unlisted = { lines: number } | 'unknown' | null;

const HUNK = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

/** A unified-diff patch's change runs (consecutive removed and added lines), each its removed (old side) and added (new side) line numbers. */
export function patchChanges(patch: string): ReviewChange[] {
  const out: ReviewChange[] = [];
  let o = 0, n = 0, hunk = -1;
  let run: ReviewChange | null = null;
  for (const line of patch.split('\n')) {
    const h = HUNK.exec(line);
    if (h) {
      o = Number(h[1]);
      n = Number(h[2]);
      hunk++;
      run = null;
      continue;
    }
    const removed = line.startsWith('-'), added = line.startsWith('+');
    if (removed || added) {
      if (!run) out.push((run = { block: hunk, run: out.filter((c) => c.block === hunk).length, old: [], new: [] }));
      if (removed) run.old.push(o++);
      else run.new.push(n++);
    } else if (!line.startsWith('\\')) {
      o++;
      n++;
      run = null;
    }
  }
  return out;
}

const range = (count: number) => Array.from({ length: count }, (_, i) => i + 1);
const oldPath = (f: BranchFile) => f.from ?? f.path;

/** The changes of `f` that count; null where they are not known without its diff (changed, with no patch). */
function changesOf(f: BranchFile): ReviewChange[] | null {
  if (f.patch) return patchChanges(f.patch);
  // A rename without edits, a mode change: no line changed.
  if (f.additions + f.deletions === 0) return [];
  if (f.status === 'added') return [{ block: 0, run: 0, old: [], new: range(f.additions) }];
  if (f.status === 'removed') return [{ block: 0, run: 0, old: range(f.deletions), new: [] }];
  return null;
}

/** The paths, at the merge base, whose old blob the share needs: each file's that existed there, by the name it had. */
export function oldSides(files: readonly BranchFile[]): string[] {
  return files.filter((f) => f.status !== 'added').map(oldPath);
}

/** The (path, blob) pairs whose marks the share counts: each new blob, and each old blob known (`oldBlobs`, by old path). */
export function sidesShown(files: readonly BranchFile[], oldBlobs: ReadonlyMap<string, string>): { path: string; blob: string }[] {
  const out: { path: string; blob: string }[] = [];
  for (const f of files) {
    const changes = changesOf(f);
    if (!changes?.length) continue;
    if (f.blob && changes.some((c) => c.new.length)) out.push({ path: f.path, blob: f.blob });
    const old = oldBlobs.get(oldPath(f));
    if (old && changes.some((c) => c.old.length)) out.push({ path: oldPath(f), blob: old });
  }
  return out;
}

/** The compare page's identity of `f` in base…head (reviewStatus `baseHeadIdentity`), by which its kept status is found. */
export function identityOf(f: BranchFile): string {
  return baseHeadIdentity({ path: f.path, status: f.status, newSha: f.blob, patch: f.patch });
}

/**
 * The status the compare page kept for `f`, when it was computed for the
 * marks on its paths now (`pathMarksSignature`, as the Files view checks
 * its own): a status kept for other marks (one removed since, or one this
 * tab has not looked up) is not taken, so it never counts more than the
 * marks now cover.
 */
export function keptFor(f: BranchFile, kept: ReadonlyMap<string, FileStatus>, marks: readonly Anchor[]): FileStatus | undefined {
  const k = kept.get(identityOf(f));
  return k && k.marks === pathMarksSignature(marks, f.from ? [f.path, f.from] : [f.path]) ? k : undefined;
}

/** `f` as the review model reads it. */
export function reviewFileOf(f: BranchFile, oldBlobs: ReadonlyMap<string, string>): ReviewFile {
  const renamedFrom = f.from ? [f.from] : [];
  const old = f.status === 'added' ? undefined : oldBlobs.get(oldPath(f));
  const changes = changesOf(f);
  return {
    path: f.path,
    old: old ? { path: oldPath(f), blob: old, renamedFrom } : null,
    new: f.blob && f.status !== 'removed' ? { path: f.path, blob: f.blob, renamedFrom } : null,
    changes,
    ...(changes ? {} : { estimate: f.additions + f.deletions }),
  };
}

export interface ShareInputs {
  files: readonly BranchFile[];
  oldBlobs: ReadonlyMap<string, string>;
  unlisted: Unlisted;
}

/**
 * The share of the files' changed lines the reviewed marks (`marks`)
 * cover: the review model's count per file, or the status the compare
 * page kept for it (`kept`), when kept for these marks (`keptFor`). A
 * side whose marks are not answered yet (`answered`), or whose old blob is
 * not read, leaves `known` false.
 */
export function reviewedShare(
  { files, oldBlobs, unlisted }: ShareInputs,
  marks: readonly Anchor[],
  answered: (path: string, blob: string) => boolean,
  kept: ReadonlyMap<string, FileStatus> = new Map(),
): ReviewedShare {
  const reviewFiles = files.map((f) => reviewFileOf(f, oldBlobs));
  const model = reviewModel({ files: reviewFiles, lineMaps: new Map() }, marks);
  let done = 0, total = 0, known = true, lowerBound = false;
  files.forEach((f, i) => {
    const r = reviewFiles[i];
    const own = model.files[i];
    const k = keptFor(f, kept, marks);
    if (k) {
      total += k.total;
      done += k.done;
      return;
    }
    total += own.total;
    // A mark on the file's paths but on neither side's blob is on an earlier one, which only a line map carries.
    const blobs = new Set([r.old?.blob, r.new?.blob]);
    const paths = new Set([f.path, oldPath(f)]);
    if (own.total > own.done && marks.some((m) => paths.has(m.path) && !blobs.has(m.blob))) lowerBound = true;
    if (r.changes === null) return;
    done += own.done;
    // A side with changed lines counts once its blob is known and its marks answered.
    for (const side of ['old', 'new'] as const) {
      if (!r.changes.some((c) => c[side].length)) continue;
      const at = r[side];
      if (!at || !answered(at.path, at.blob)) known = false;
    }
  });
  if (unlisted && unlisted !== 'unknown') total += Math.max(0, unlisted.lines);
  return { done, total, known, firstFiles: unlisted === 'unknown', lowerBound };
}
